import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FIELDS = [
  { key: "generation_enabled", label: "Generation enabled", type: "bool" },
  { key: "kill_switch_reason", label: "Kill-switch message (shown to users when paused)", type: "text" },
  { key: "daily_cost_limit_usd", label: "Global daily cost limit (USD)", type: "number", step: 0.5 },
  { key: "cost_per_prayer_usd", label: "Estimated cost per prayer (USD)", type: "number", step: 0.01 },
  { key: "user_daily_limit", label: "Registered free: prayers per day", type: "number", step: 1 },
  { key: "guest_total_limit", label: "Guest: total prayers", type: "number", step: 1 },
  { key: "user_cooldown_seconds", label: "User cooldown (seconds)", type: "number", step: 5 },
  { key: "guest_cooldown_seconds", label: "Guest cooldown (seconds)", type: "number", step: 5 },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user.email !== "admin@inlight.app") {
      setForbidden(true);
      return;
    }
    Promise.all([api.get("/admin/settings"), api.get("/admin/usage")])
      .then(([s, u]) => {
        setSettings(s.data);
        setUsage(u.data);
        setDraft(s.data);
      })
      .catch(() => setForbidden(true));
  }, [user, loading]);

  if (forbidden) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
        <Header />
        <div className="max-w-md mx-auto pt-32 px-6 text-center" data-testid="admin-forbidden">
          <h1 className="font-serif-display text-3xl mb-4" style={{ color: "var(--sanctuary-ink)" }}>This page is for the keeper</h1>
          <p style={{ color: "var(--sanctuary-ink-soft)" }}>
            Please sign in as the admin to view this page.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-8 px-6 py-3 rounded-full btn-primary"
            data-testid="admin-go-home"
          >
            Return home
          </button>
        </div>
      </div>
    );
  }

  if (!settings || !usage) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
        <Header />
        <p className="text-center pt-32 font-serif-display italic" style={{ color: "var(--sanctuary-ink-muted)" }}>
          Loading…
        </p>
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      const update = {};
      for (const f of FIELDS) {
        const v = draft[f.key];
        if (f.type === "number") update[f.key] = Number(v);
        else if (f.type === "bool") update[f.key] = !!v;
        else update[f.key] = String(v ?? "");
      }
      const { data } = await api.put("/admin/settings", update);
      setSettings(data);
      setDraft(data);
      setToast("Saved");
      setTimeout(() => setToast(""), 2000);
    } finally {
      setBusy(false);
    }
  };

  const today = usage.today_brisbane;
  const used = today.est_cost || 0;
  const pct = Math.min(100, (used / Math.max(0.0001, settings.daily_cost_limit_usd)) * 100);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-16 pb-24">
        <p className="text-[11px] tracking-[0.35em] uppercase mb-3" style={{ color: "var(--sanctuary-gold)" }}>
          The keeper's panel
        </p>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-12 tracking-tight" style={{ color: "var(--sanctuary-ink)" }} data-testid="admin-title">
          Inlight settings & usage
        </h1>

        {/* Today */}
        <section className="mb-14 p-7 rounded-2xl" style={{ background: "var(--sanctuary-paper)", border: "1px solid #E8E4D9" }} data-testid="admin-today-card">
          <p className="text-xs tracking-widest uppercase mb-3" style={{ color: "var(--sanctuary-ink-muted)" }}>
            Today · Brisbane · {today.date}
          </p>
          <div className="flex items-baseline justify-between mb-4">
            <span className="font-serif-display text-3xl" style={{ color: "var(--sanctuary-ink)" }} data-testid="admin-prayers-today">
              {today.total_prayers || 0} prayers
            </span>
            <span className="font-serif-display text-2xl" style={{ color: "var(--sanctuary-gold)" }} data-testid="admin-cost-today">
              ${used.toFixed(2)} / ${settings.daily_cost_limit_usd.toFixed(2)}
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--sanctuary-border)" }}>
            <div
              className="h-full"
              style={{ width: `${pct}%`, background: pct >= 100 ? "var(--sanctuary-alert)" : "var(--sanctuary-accent)", transition: "width 0.3s ease" }}
            />
          </div>
          <p className="mt-4 text-sm" style={{ color: "var(--sanctuary-ink-soft)" }}>
            Generation is currently{" "}
            <strong style={{ color: settings.generation_enabled ? "var(--sanctuary-sage)" : "var(--sanctuary-alert)" }} data-testid="admin-enabled-state">
              {settings.generation_enabled ? "ON" : "PAUSED"}
            </strong>.
          </p>
          <p className="mt-2 text-xs" style={{ color: "var(--sanctuary-ink-muted)" }}>
            {usage.user_count} registered accounts.
          </p>
        </section>

        {/* Settings form */}
        <section data-testid="admin-settings-form">
          {FIELDS.map((f) => (
            <div key={f.key} className="mb-8">
              <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: "var(--sanctuary-ink-muted)" }}>
                {f.label}
              </label>
              {f.type === "bool" ? (
                <button
                  onClick={() => setDraft((d) => ({ ...d, [f.key]: !d[f.key] }))}
                  data-testid={`admin-toggle-${f.key}`}
                  className="px-5 py-2.5 rounded-full text-sm transition"
                  style={{
                    backgroundColor: draft[f.key] ? "var(--sanctuary-accent)" : "white",
                    color: draft[f.key] ? "white" : "var(--sanctuary-ink)",
                    border: `1px solid ${draft[f.key] ? "var(--sanctuary-accent)" : "var(--sanctuary-border)"}`,
                  }}
                >
                  {draft[f.key] ? "ON" : "OFF"}
                </button>
              ) : (
                <input
                  data-testid={`admin-input-${f.key}`}
                  type={f.type === "number" ? "number" : "text"}
                  step={f.step}
                  value={draft[f.key] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: f.type === "number" ? e.target.value : e.target.value }))}
                  className="input-line"
                />
              )}
            </div>
          ))}
          <button
            onClick={save}
            disabled={busy}
            data-testid="admin-save"
            className="mt-4 px-8 py-3.5 rounded-full btn-primary text-base disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save settings"}
          </button>
          {toast && <span className="ml-4 text-sm" style={{ color: "var(--sanctuary-sage)" }} data-testid="admin-toast">{toast}</span>}
        </section>

        {/* Recent blocked / failed */}
        <section className="mt-16">
          <h2 className="font-serif-display text-2xl mb-4" style={{ color: "var(--sanctuary-ink)" }}>Recent blocked or failed</h2>
          <div className="space-y-2 text-sm" data-testid="admin-recent-events">
            {usage.recent_blocked_or_failed.length === 0 && (
              <p style={{ color: "var(--sanctuary-ink-muted)" }} className="italic">No blocked or failed events recently.</p>
            )}
            {usage.recent_blocked_or_failed.map((e, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg" style={{ background: "var(--altar-surface)" }}>
                <span style={{ color: "var(--sanctuary-ink-soft)" }}>
                  <strong style={{ color: "var(--sanctuary-ink)" }}>{e.kind}</strong>
                  {" · "}
                  {e.reason || e.stage || ""}
                </span>
                <span className="text-xs" style={{ color: "var(--sanctuary-ink-muted)" }}>{(e.at || "").slice(0, 19).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </section>

        {/* History */}
        <section className="mt-12">
          <h2 className="font-serif-display text-2xl mb-4" style={{ color: "var(--sanctuary-ink)" }}>Last 14 days</h2>
          <div className="space-y-1 text-sm" data-testid="admin-history">
            {usage.history.map((h) => (
              <div key={h.date} className="flex items-center justify-between p-2">
                <span style={{ color: "var(--sanctuary-ink-soft)" }}>{h.date}</span>
                <span style={{ color: "var(--sanctuary-ink)" }}>{h.total_prayers || 0} prayers · ${(h.est_cost || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
